// Partial FD closure and Close FD verification (legacy /AccountsApproval/VerifyPartialFD and
// /AccountsApproval/VerifyCloseFD — legacy renders both with VerifyPartialFDView; fdMode picks the routes).
// Shows the maturity / interest split, the FD left after the partial closure, deductions and the
// bank payment; verified/approved via the shared status list (legacy drops "Return" here).
// Rows returned for update (Status '0') are corrected on the Partial FD entry screen, as in legacy.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PiggyBank, Clock, Building2, RotateCcw } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyPartialFDQueue,
    fetchVerifyPartialFDDetail,
    submitApprovePartialFD,
    clearVerifyPartialFDDetail,
    resetVerifyPartialFD,
    selectVPFQueue, selectVPFQueueLoading, selectVPFQueueError,
    selectVPFDetail, selectVPFDetailLoading, selectVPFDetailError,
    selectVPFVerifyLoading,
} from '../../slices/accountsSlice/verifyPartialFDSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

// spApprovePartialFD posts the principal to the capital cost center.
const CAPITAL_CC = 'CCC';

const MODE_TEXT = {
    Partial: { title: 'Partial FD Verification', noun: 'partial FD', closure: 'Partial Closure', entry: 'Partial FD' },
    Close: { title: 'Close FD Verification', noun: 'FD closure', closure: 'Closure', entry: 'Close FD' },
};

const Field = ({ label, value, className = '', highlight = false }) => (
    <div className={className}>
        <p className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${highlight ? 'text-green-700 dark:text-green-400' : 'text-gray-400'}`}>{label}</p>
        <p className={`text-sm font-semibold break-words ${highlight ? 'text-green-700 dark:text-green-400' : 'text-gray-800 dark:text-gray-100'}`}>{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const num = (v) => parseFloat(v) || 0;
const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);

const VerifyPartialFD = ({ fdMode = 'Partial', notificationData, onNavigate }) => {
    const text = MODE_TEXT[fdMode] || MODE_TEXT.Partial;
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVPFQueue);
    const queueLoading = useSelector(selectVPFQueueLoading);
    const queueError = useSelector(selectVPFQueueError);
    const detail = useSelector(selectVPFDetail);
    const detailLoading = useSelector(selectVPFDetailLoading);
    const detailError = useSelector(selectVPFDetailError);
    const verifyLoading = useSelector(selectVPFVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Status) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const fd = selectedItem && detail?.BankTransactionRefNo === selectedItem.BankTransactionRefNo ? detail : null;
    const deductions = fd?.FDDeductionList || [];
    const deductionTotal = deductions.reduce((sum, d) => sum + num(d.DeductionValue), 0);

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyPartialFDQueue({ roleId, mode: fdMode }));
    }, [roleId, fdMode, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyPartialFD());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyPartialFDDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        if (String(selectedItem.Status) !== '0') dispatch(fetchVerifyPartialFDDetail({ transactionNo: selectedItem.BankTransactionRefNo, mode: fdMode }));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the detail does. Remarks are keyed on the bank transaction ref no.
    useEffect(() => {
        if (!fd?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: fd.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(fd.MOID));
        dispatch(fetchRemarks({ trno: fd.BankTransactionRefNo, moid: fd.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fd?.MOID, fd?.BankTransactionRefNo]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyPartialFDQueue({ roleId, mode: fdMode }));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyPartialFDQueue({ roleId, mode: fdMode }));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyPartialFDDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!fd) { toast.error(`No ${text.noun} selected`); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const csv = (key) => deductions.map((d) => d[key]).join(',') + (deductions.length ? ',' : '');
        const payload = {
            BankTransactionRefNo: fd.BankTransactionRefNo,
            Action: actionValue,
            ApprovalNote: comment.trim(),
            Bankid: fd.Bankid,
            Roleid: roleId,
            Createdby: getCurrentUser(),
            PaymentAmount: fd.FDRAmount,
            FDRClosingDate: fd.FDRClosingDate,
            DedDcas: csv('DCACode'),
            DedSDcas: csv('SubDCACode'),
            DedAmounts: csv('DeductionValue'),
            Capitalcc: CAPITAL_CC,
            FDRNo: fd.FDRNo,
            Fdtype: fd.Fdtype,
            // Legacy computed this from a field that no longer exists (always NaN → null); the SP does
            // not read it, but send the intended maturity + interest − deductions figure.
            Amount: num(fd.Amount) + num(fd.IntAmount) - deductionTotal,
        };

        try {
            const result = await dispatch(submitApprovePartialFD(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`${text.entry} ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            // Budget / permission problems come back through the same status string.
            toast.error(status || 'Error Occurred');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.FDRNo ?? '').toLowerCase().includes(q)
            || item.FDRClosingDate?.toLowerCase().includes(q)
            || String(item.FDRAmount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <PiggyBank className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">FD {item.FDRNo}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Closing {item.FDRClosingDate}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.FDRAmount)}</span>
            </div>
            <div className="flex justify-end"><StatusBadge status={item.Status} /></div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[140px]">FD {item.FDRNo}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[140px]">Closing {item.FDRClosingDate}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.FDRAmount)}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <PiggyBank className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => {
        const newFd = fd.NewFDDetails;
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Field label="FD Number" value={fd.FDRNo} />
                    <Field label="Closing Date" value={fd.FDRClosingDate} />
                    <Field label="Maturity Amount" value={formatAmount(fd.Amount)} />
                    <Field label="Interest" value={formatAmount(fd.IntAmount)} />
                    {fd.ReturnBudgetFYYear && <Field label="Budget Return FY Year" value={fd.ReturnBudgetFYYear} highlight />}
                </div>

                {newFd && (newFd.NewFDRAmount != null || newFd.NewFDBalance != null) && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">FD After Partial Closure</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <Field label="Begin Date" value={newFd.NewFDRFromDate} />
                            <Field label="End Date" value={newFd.NewFDRToDate} />
                            <Field label="Amount" value={formatAmount(newFd.NewFDRAmount)} />
                            <Field label="Balance" value={formatAmount(newFd.NewFDBalance)} />
                            <Field label="Rate of Interest (%)" value={newFd.NewFDRROI} />
                        </div>
                    </div>
                )}

                {deductions.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Deduction Details</p>
                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-100 dark:bg-gray-800">
                                        <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">Account Head</th>
                                        <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">Sub Account Head</th>
                                        <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deductions.map((d, i) => (
                                        <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                            <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{d.DCACode}</td>
                                            <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{d.SubDCACode}</td>
                                            <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{formatIndianCurrency(d.DeductionValue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                                        <td colSpan={2} className="px-4 py-2 text-right text-xs font-bold text-gray-600 dark:text-gray-300 uppercase">Total</td>
                                        <td className="px-4 py-2 text-right font-bold text-gray-900 dark:text-white">{formatIndianCurrency(deductionTotal)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Field label="Bank" value={fd.BankName} />
                    <Field label="Mode of Pay" value={fd.ModeofPay} />
                    <Field label="No" value={fd.No} />
                    <Field label="Payment Date" value={fd.PaymentDate} />
                    <Field label="Amount Credited" value={formatAmount(fd.FDRAmount)} />
                    <Field label="Remarks" value={fd.Remarks} className="sm:col-span-2 lg:col-span-3" />
                </div>
            </div>
        );
    };

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this {text.noun}</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (isReturned) {
            return (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700 flex items-start gap-3">
                    <RotateCcw className="w-5 h-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">FD {selectedItem.FDRNo} was returned for update</h3>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">Correct and resubmit it from the {text.entry} entry screen — there is nothing to verify until it is resubmitted.</p>
                    </div>
                </div>
            );
        }
        if (detailLoading || (!fd && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading {text.noun} details…</span>
                </div>
            );
        }
        if (detailError || !fd) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || `${text.entry} details could not be loaded — it may no longer be pending at your level.`}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">FD {fd.FDRNo} — {text.closure}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(fd.FDRAmount)} credited</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {fd.BankName}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Closing {fd.FDRClosingDate}</span>
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />
                {renderActions()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || text.title} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={PiggyBank}
                badgeText={text.title}
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by FD number, closing date, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: `No ${text.noun}s pending verification!`,
                        itemKey: 'BankTransactionRefNo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: text.title,
                        icon: PiggyBank,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: `${text.entry} Details`,
                        icon: PiggyBank,
                        selectedTitle: text.title,
                        emptyTitle: `No ${text.entry} Selected`,
                        emptyMessage: `Select a ${text.noun} from the list to view details and take action.`,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyPartialFD;
